import type { ReactElement } from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import apiDocsNativePlugin from '@backstage/plugin-api-docs/alpha';
import {
  githubAuthApiRef,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import type { SignInProviderConfig } from '@backstage/core-components';
import { CatalogEntityPage, catalogPlugin } from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
// <ScaffolderPage> MUST be inlined directly as the `element=` value of its
// <Route>. FlatRoutes' routable-extension discovery walks the literal element
// tree passed to <Route element>; wrapping ScaffolderPage in any other
// component (BlueprintsPage, BlueprintsTemplatesTab, etc.) hides its routeRef
// from discovery and breaks `bind(catalogPlugin.externalRoutes, ...)`.
import { BlueprintsTemplateListPage } from './components/scaffolder/BlueprintsTemplatesTab';
import TasksListPage from './components/scaffolder/TasksListPage';
import ActionsCatalogPage from './components/scaffolder/ActionsCatalogPage';
import {
  CustomFieldsPage,
  AuthoringGuidePage,
} from './components/scaffolder/blueprintRoutes';
import { EntityLinkTypePickerFieldExtension } from './components/scaffolder/EntityLinkTypes';
import { UniqueEntityNameFieldExtension } from './components/scaffolder/UniqueEntityName';
import { ProjectUploadFieldExtension } from './components/scaffolder/ProjectUpload';
import { PendingVibeUpdatePickerFieldExtension } from './components/scaffolder/PendingVibeUpdatePicker';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import techdocsNativePlugin from '@backstage/plugin-techdocs/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import signalsPlugin from '@backstage/plugin-signals/alpha';
import { techDocsReportIssueAddonModule } from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import { techDocsCopyHeadingLinkAddonModule } from './components/techdocs/CopyHeadingLink';
import { apis } from './apis';
import { searchInsightsModule } from './analytics/searchInsightsModule';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { MakeSignInPage } from './components/signin/MakeSignInPage';
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid/alpha';

import {
  AlertDisplay,
  coreComponentsTranslationRef,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import {
  createFrontendModule,
  createTranslationMessages,
} from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';
import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import HomePage from './components/home/HomePage';
import { MakeCatalogIndexPage } from './components/catalog/CatalogPageExtension';
import { CATALOG_PATHS } from './components/catalog/catalogNavigation';

import { configApiRef, useApi } from '@backstage/core-plugin-api';

import { UnifiedThemeProvider } from '@backstage/theme';
import { makeDarkTheme, makeTheme } from './theme/make';
import cloudflareWorkersPlugin from '@internal/plugin-cloudflare-workers';
import dependencySecurityPlugin from '@internal/plugin-dependency-security';
import { LockManagerPage } from '@internal/plugin-lock-manager';
import makeReviewPlugin from '@internal/plugin-make-review';
import makeShellPlugin from '@internal/plugin-make-shell';
import { getFlag } from './helpers/helpers';
import devboxManagerPlugin from '@internal/backstage-plugin-devbox-manager';
import contextRouterPlugin from '@internal/plugin-context-router';
import aiSpendMonitorPlugin from '@internal/plugin-ai-spend-monitor';
import soc2ReportPlugin from '@internal/plugin-soc2-report';
import cloudAgentsPlugin from '@internal/plugin-cloud-agents';
import nodeInventoryPlugin from '@internal/plugin-node-inventory';
import versionsManagerPlugin from '@internal/plugin-versions-manager';
import deploymentsPlugin from '@internal/plugin-deployments';
import kargoPlugin from '@internal/backstage-plugin-kargo';
import { OAuthConsentPage } from './components/OAuthConsent';
import { MakeSettingsPage } from './components/settings/SettingsPageExtension';

const legacyOptionsModule = convertLegacyAppOptions({
  apis,
  themes: [
    // The first theme in this list is the default: with no stored preference
    // the app picks the first 'dark'-variant theme (or the first theme
    // overall). Don't use the reserved ids 'light'/'dark' here — those make
    // convertLegacyAppOptions override the builtin theme slots, which sort
    // before appended themes and would demote 'make' from the default spot.
    {
      id: 'make',
      title: 'Make',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={makeTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'default-dark',
      title: 'Default dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={makeDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  ],
  components: {
    SignInPage: props => {
      const config = useApi(configApiRef);

      const localGithubCliAuthEnabled = getFlag(config, 'localGithubCliAuth');
      const githubProvider: SignInProviderConfig = {
        id: 'github-auth-provider',
        title: 'GitHub',
        message: 'Full portal access',
        apiRef: githubAuthApiRef,
      };

      const providers: SignInProviderConfig[] = [];
      if (getFlag(config, 'githubAuth') || localGithubCliAuthEnabled) {
        providers.push(githubProvider);
      }
      if (getFlag(config, 'microsoftAuth')) {
        providers.push({
          id: 'microsoft-auth-provider',
          title: 'Microsoft',
          message: 'Vibe-coding platform only',
          apiRef: microsoftAuthApiRef,
        });
      }

      // No SSO provider configured (local dev): fall back to guest login.
      if (providers.length === 0) {
        return <SignInPage {...props} auto providers={['guest']} />;
      }

      // The built-in page refreshes silently first. Local auth must not open
      // a popup automatically if that refresh fails.
      if (providers.length === 1) {
        return (
          <SignInPage
            {...props}
            auto={!localGithubCliAuthEnabled}
            provider={providers[0]}
          />
        );
      }

      return (
        <MakeSignInPage
          {...props}
          providers={providers}
          silentSignInProviderId={
            localGithubCliAuthEnabled ? githubProvider.id : undefined
          }
        />
      );
    },
  },
});

export const routes: ReactElement = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path={CATALOG_PATHS.root} element={<MakeCatalogIndexPage />} />

    <Route
      path={`${CATALOG_PATHS.root}/:namespace/:kind/:name`}
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    {/*
     * Blueprints hub — sibling routes so <ScaffolderPage> stays a direct
     * child of <FlatRoutes> and its routeRef is discoverable by Backstage.
     *
     * Why sibling routes, not a wrapper component:
     *   Backstage's FlatRoutes traversal requires routable extensions
     *   (ScaffolderPage) to be direct <Route element> values — not buried
     *   inside an inner <Routes> tree. Wrapping in BlueprintsPage broke
     *   `bind(catalogPlugin.externalRoutes, { createComponent: ... })`.
     *
     * React Router v6 specificity ensures static paths win over wildcards:
     *   /create/tasks         → TasksListPage        (our custom list)
     *   /create/actions       → ActionsCatalogPage   (our custom catalog)
     *   /create/custom-fields → CustomFieldsPage     (wrapper + registry)
     *   /create/guide         → AuthoringGuidePage   (wrapper + markdown)
     *   /create               → ScaffolderPage       (routable extension with
     *                            BlueprintsTemplateListPage injected for the
     *                            root templates list; also serves /create/edit,
     *                            /create/tasks/:taskId, /create/templates/…)
     *
     * NOTE: <FlatRoutes> rewrites every route path to `${path}/*`, so the
     * /create/tasks route also matches /create/tasks/:taskId. TasksListPage
     * owns that detail route internally and renders the OOTB <TaskPage> (with
     * run logs) for it — it does NOT fall through to ScaffolderPage.
     *
     * Field extensions (EntityLinkTypePicker, UniqueEntityName, ProjectUpload)
     * remain direct props.children of the inline <ScaffolderPage> below.
     */}
    <Route path="/create/tasks" element={<TasksListPage />} />
    <Route path="/create/actions" element={<ActionsCatalogPage />} />
    <Route path="/create/custom-fields" element={<CustomFieldsPage />} />
    <Route path="/create/guide" element={<AuthoringGuidePage />} />
    {/*
     * <ScaffolderPage> is inlined here, NOT wrapped. FlatRoutes scans the
     * static element tree for routable extensions; wrapping ScaffolderPage in
     * any other component (even one that returns just <ScaffolderPage>) hides
     * its routeRef from discovery and breaks catalog → "Create Component".
     */}
    <Route
      path="/create"
      element={
        <ScaffolderPage
          headerOptions={{
            title: 'Blueprints',
            subtitle: 'Reusable templates for components, workflows and infra',
            pageTitleOverride: 'Blueprints',
          }}
          components={{
            EXPERIMENTAL_TemplateListPageComponent: BlueprintsTemplateListPage,
          }}
          contextMenu={{
            editor: true,
            actions: true,
            tasks: true,
            create: true,
            templatingExtensions: false,
          }}
        >
          <ScaffolderFieldExtensions>
            <EntityLinkTypePickerFieldExtension />
            <UniqueEntityNameFieldExtension />
            <ProjectUploadFieldExtension />
            <PendingVibeUpdatePickerFieldExtension />
          </ScaffolderFieldExtensions>
        </ScaffolderPage>
      }
    />
    <Route
      path={CATALOG_PATHS.import}
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<MakeSettingsPage />} />
    <Route path={CATALOG_PATHS.graph} element={<CatalogGraphPage />} />
    <Route path="/lock-manager" element={<LockManagerPage />} />
    <Route path="/oauth2/authorize/:sessionId" element={<OAuthConsentPage />} />
  </FlatRoutes>
);

const appModuleTranslations = createFrontendModule({
  pluginId: 'app',
  featureFlags: [
    {
      name: 'sidebar-legacy',
      description:
        'Go back to the previous sidebar for now. The new sidebar is the default and the previous one will be removed. Reload the page after changing this flag.',
    },
    {
      name: 'notifications',
      description:
        'Show a notifications bell in the new sidebar, next to Settings: reviews requested from you and reviews on your pull requests, updated live. Not shown with the previous sidebar ("sidebar-legacy"). Reload the page after changing this flag.',
    },
    {
      name: 'home-needs-you',
      description:
        'Replace "Your open pull requests" on the home page with "Your work": a "Needs you" list (reviews requested from you, security findings in your services, failed Blueprint runs, your locks, devboxes and internal apps) next to your open pull requests. Reload the page after changing this flag.',
    },
  ],
  extensions: [
    TranslationBlueprint.make({
      name: 'core-components-overrides',
      params: {
        resource: createTranslationMessages({
          ref: coreComponentsTranslationRef,
          messages: { 'table.toolbar.search': 'Search' },
        }),
      },
    }),
  ],
});

const app = createApp({
  features: [
    appModuleTranslations,
    legacyOptionsModule,
    searchInsightsModule,
    devboxManagerPlugin,
    contextRouterPlugin,
    makeShellPlugin,
    aiSpendMonitorPlugin,
    soc2ReportPlugin,
    cloudAgentsPlugin,
    cloudflareWorkersPlugin,
    nodeInventoryPlugin,
    makeReviewPlugin,
    versionsManagerPlugin,
    deploymentsPlugin,
    kargoPlugin,
    dependencySecurityPlugin,
    notificationsPlugin,
    signalsPlugin,
    ...convertLegacyAppRoot(
      <>
        <AlertDisplay />
        <OAuthRequestDialog />
        <AppRouter>
          <Root>{routes}</Root>
        </AppRouter>
      </>,
    ),
    // The API explorer runs on the native frontend system, whose /api-docs
    // page uses BUI's page header. It must come after the converted legacy
    // root: the legacy entity page's API cards make the converter emit an
    // 'api-docs' plugin too, and of two plugins with the same id the app keeps
    // the later one.
    apiDocsNativePlugin,
    // TechDocs runs on the native frontend system, whose /docs index and
    // reader pages use BUI's header and layout; the index's initial filter is
    // set in app-config (app.extensions → page:techdocs). It must come after
    // the converted legacy root: the legacy entity page's Docs tab makes the
    // converter emit a 'techdocs' plugin too, and of two plugins with the same
    // id the app keeps the later one.
    techdocsNativePlugin,
    techDocsMermaidAddonModule,
    techDocsReportIssueAddonModule,
    techDocsCopyHeadingLinkAddonModule,
  ],
  bindRoutes({ bind }) {
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(
        scaffolderPlugin.routes.selectedTemplate,
      ),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(
        catalogImportPlugin.routes.importPage,
      ),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
  },
});

export default app.createRoot();
